import { create } from "zustand"
import { createJSONStorage, devtools, persist } from "zustand/middleware"
import { APP_ID } from "../utils/constants"

/**
 * Collapsed/expanded state of the admin side navigation.
 *
 * Expanded by default. Kept in sessionStorage, so a toggle survives reloads
 * and navigation within the tab but every new tab starts expanded again.
 *
 * Only applies on md+ screens; below that the sidebar is a drawer driven by
 * `isSidebarOpen` in the layout. The width itself is published as the
 * `--sidenav-w` CSS variable (see index.css) so the layout, fixed footers and
 * overlays that clear the sidebar all move with it.
 */
interface SideNavigationState {
  collapsed: boolean
  setCollapsed: (collapsed: boolean) => void
  toggleCollapsed: () => void
}

export const useSideNavigationStore = create<SideNavigationState>()(
  devtools(
    persist(
      (set) => ({
        collapsed: false,
        setCollapsed: (collapsed) => set({ collapsed }, false, "setCollapsed"),
        toggleCollapsed: () =>
          set(
            (state) => ({ collapsed: !state.collapsed }),
            false,
            "toggleCollapsed"
          ),
      }),
      {
        name: `${APP_ID}.side-navigation`,
        storage: createJSONStorage(() => sessionStorage),
      }
    ),
    { name: "SideNavigationStore" }
  )
)

// The layouts keep the attribute in sync while mounted (useSideNavCollapse),
// but the boot skeleton paints before any of them do. Applying the persisted
// value up front keeps its sidebar placeholder the same width as the real one.
if (
  typeof document !== "undefined" &&
  useSideNavigationStore.getState().collapsed
) {
  document.documentElement.setAttribute("data-sidenav", "collapsed")
}
